import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { debounce } from 'lodash';
import clsx from 'clsx';

type Props = {
  className?: string;
  search: string;
  onSearch: (value: string) => void;
};

const SearchToggle = ({ className, onSearch }: Props) => {
  const [open, setOpen] = useState<boolean>(false);
  const [value, setValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSearchDebounce = useRef(
    debounce(async value => {
      await onSearch(value);
    }, 600)
  ).current;

  useEffect(() => {
    return () => {
      handleSearchDebounce.cancel();
    };
  }, [handleSearchDebounce]);

  const handleClick = useCallback(() => {
    if (!open) {
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  const handleBlur = useCallback(() => {
    if (!value) {
      setOpen(false);
    }
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    handleSearchDebounce(newValue);
  };

  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setValue('');
      onSearch('');
      inputRef.current?.focus();
    },
    [onSearch]
  );

  return (
    <div
      className={clsx(
        `
        flex justify-center items-center border rounded-lg
        transition-all duration-300 overflow-hidden border-gray-600`,
        open ? 'w-[200px] p-[10px] cursor-text' : 'w-[32px] cursor-pointer',
        'h-[32px]',
        className
      )}
      onClick={handleClick}
    >
      <Search size={16} className="text-gray-400 flex-shrink-0" />
      {open && (
        <>
          <input
            ref={inputRef}
            value={value}
            onChange={handleInputChange}
            onBlur={handleBlur}
            placeholder="Поиск"
            className="ml-2 w-full bg-transparent text-white text-sm outline-none placeholder:text-white/50"
          />
          {value && (
            <X
              size={14}
              className="text-white flex-shrink-0 cursor-pointer"
              onClick={handleClear}
            />
          )}
        </>
      )}
    </div>
  );
};

export default SearchToggle;
